import type {
  AttachmentPointDefinition,
  Catalog,
  CatalogModule,
  ExplicitDenyRule,
  ModuleId,
} from "./types.ts";

export interface CompiledCatalog {
  readonly source: Catalog;
  readonly moduleById: ReadonlyMap<ModuleId, CatalogModule>;
  /** Alias to canonical module ID. Canonical IDs are intentionally absent. */
  readonly canonicalModuleIdByAlias: ReadonlyMap<ModuleId, ModuleId>;
  readonly modulesByConnectionType: ReadonlyMap<string, readonly CatalogModule[]>;
}

export type FitResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly code: "WRONG_MODULE_ROLE" }
  | { readonly ok: false; readonly code: "CONNECTION_TYPE_MISMATCH" }
  | {
      readonly ok: false;
      readonly code: "EXPLICITLY_DENIED";
      readonly rule: ExplicitDenyRule;
    };

export function compileCatalog(catalog: Catalog): CompiledCatalog {
  const moduleById = new Map<ModuleId, CatalogModule>();
  const canonicalModuleIdByAlias = new Map<ModuleId, ModuleId>();
  const modulesByConnectionType = new Map<string, CatalogModule[]>();

  for (const module of catalog.modules) {
    if (module.id.trim().length === 0 || module.id !== module.id.trim()) {
      throw new Error(`Catalog module id must be a non-empty trimmed string: ${JSON.stringify(module.id)}`);
    }
    if (moduleById.has(module.id)) {
      throw new Error(`Duplicate catalog module id: ${module.id}`);
    }
    moduleById.set(module.id, module);
  }

  // Two passes make aliases unable to claim a canonical ID that appears later.
  for (const module of catalog.modules) {
    for (const alias of module.aliases ?? []) {
      if (alias.trim().length === 0 || alias !== alias.trim()) {
        throw new Error(
          `Catalog module alias must be a non-empty trimmed string on ${module.id}: ${JSON.stringify(alias)}`,
        );
      }
      if (moduleById.has(alias)) {
        throw new Error(`Catalog module alias collision: ${alias} is already a canonical module id`);
      }
      const previousOwner = canonicalModuleIdByAlias.get(alias);
      if (previousOwner != null) {
        throw new Error(
          `Duplicate catalog module alias ${alias}: owned by ${previousOwner} and ${module.id}`,
        );
      }
      canonicalModuleIdByAlias.set(alias, module.id);
    }
  }

  for (const module of catalog.modules) {

    const pointIds = new Set<string>();
    for (const point of module.attachmentPoints) {
      if (pointIds.has(point.id)) {
        throw new Error(`Duplicate attachment point ${point.id} on ${module.id}`);
      }
      pointIds.add(point.id);
      if (point.visualAnchor.trim().length === 0) {
        throw new Error(`Attachment point ${module.id}/${point.id} needs an exact visualAnchor`);
      }
    }

    if (module.role === "module") {
      if (module.connectionType == null || module.connectionType.length === 0) {
        throw new Error(`Child module ${module.id} needs a connectionType`);
      }
      const bucket = modulesByConnectionType.get(module.connectionType) ?? [];
      bucket.push(module);
      modulesByConnectionType.set(module.connectionType, bucket);
    }
  }

  if (![...moduleById.values()].some((module) => module.role === "base")) {
    throw new Error("Catalog needs at least one base module");
  }

  return { source: catalog, moduleById, canonicalModuleIdByAlias, modulesByConnectionType };
}

export function canonicalModuleId(
  catalog: CompiledCatalog,
  authoredId: ModuleId,
): ModuleId | undefined {
  if (catalog.moduleById.has(authoredId)) return authoredId;
  return catalog.canonicalModuleIdByAlias.get(authoredId);
}

export function pointDefinition(
  module: CatalogModule,
  pointId: string,
): AttachmentPointDefinition | undefined {
  return module.attachmentPoints.find((point) => point.id === pointId);
}

export function checkFit(point: AttachmentPointDefinition, module: CatalogModule): FitResult {
  if (module.role !== "module") return { ok: false, code: "WRONG_MODULE_ROLE" };
  if (module.connectionType == null || !point.accepts.includes(module.connectionType)) {
    return { ok: false, code: "CONNECTION_TYPE_MISMATCH" };
  }
  const rule = point.deny?.find((candidate) => candidate.moduleId === module.id);
  if (rule != null) return { ok: false, code: "EXPLICITLY_DENIED", rule };
  return { ok: true };
}

export function compatibleModules(
  catalog: CompiledCatalog,
  point: AttachmentPointDefinition,
): readonly CatalogModule[] {
  const candidates = new Map<ModuleId, CatalogModule>();
  for (const connectionType of point.accepts) {
    for (const module of catalog.modulesByConnectionType.get(connectionType) ?? []) {
      if (checkFit(point, module).ok) candidates.set(module.id, module);
    }
  }
  return [...candidates.values()].sort((left, right) => left.label.localeCompare(right.label));
}
